import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { useAdminApi } from "../../hooks/useAdminApi";

const DAYS = [
  { label: "Monday", weekday: "mon" },
  { label: "Tuesday", weekday: "tue" },
  { label: "Wednesday", weekday: "wed" },
  { label: "Thursday", weekday: "thu" },
  { label: "Friday", weekday: "fri" },
  { label: "Saturday", weekday: "sat" },
  { label: "Sunday", weekday: "sun" },
];

const emptyDoctorForm = {
  name: "",
  specialty: "",
  apmc_number: "",
  degrees: "",
  occupation: "",
  photo_url: "",
  clinic_id: null,
};

const emptyClinicForm = { name: "", address: "" };

const emptyAvailability = () =>
  DAYS.map(({ weekday }) => ({ weekday, start_time: "", end_time: "" }));

const Field = ({ label, value, onChange, placeholder, disabled }) => (
  <div className="space-y-1">
    <label className="text-sm font-medium text-gray-700">{label}</label>
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder || label}
      disabled={disabled}
    />
  </div>
);

const UnifiedDoctorForm = ({ open, doctor, onSave, onCancel }) => {
  const {
    createDoctor,
    updateDoctor,
    createClinic,
    updateClinic,
    getDoctorAvailability,
    updateDoctorAvailability,
  } = useAdminApi();

  const [doctorForm, setDoctorForm] = useState(emptyDoctorForm);
  const [clinicForm, setClinicForm] = useState(emptyClinicForm);
  const [availability, setAvailability] = useState(emptyAvailability());
  const [saving, setSaving] = useState(false);
  const [formTab, setFormTab] = useState("doctor");

  useEffect(() => {
    if (!open) return;
    if (doctor) {
      setDoctorForm({
        name: doctor.name || "",
        specialty: doctor.specialty || "",
        apmc_number: doctor.apmc_number || "",
        degrees: doctor.degrees || "",
        occupation: doctor.occupation || "",
        photo_url: doctor.photo_url || "",
        clinic_id: doctor.clinic_id || null,
      });
      setClinicForm({
        name: doctor.clinic_name || "",
        address: doctor.address || "",
      });
      getDoctorAvailability(doctor.id)
        .then(({ data }) => {
          if (Array.isArray(data) && data.length) {
            const merged = DAYS.map(({ weekday }) => {
              const found = data.find((a) => a.weekday === weekday);
              return {
                weekday,
                start_time: found?.start_time || "",
                end_time: found?.end_time || "",
              };
            });
            setAvailability(merged);
          } else {
            setAvailability(emptyAvailability());
          }
        })
        .catch(() => setAvailability(emptyAvailability()));
    } else {
      setDoctorForm(emptyDoctorForm);
      setClinicForm(emptyClinicForm);
      setAvailability(emptyAvailability());
    }
    setFormTab("doctor");
  }, [open, doctor]);

  const setDoctorField = (field) => (value) =>
    setDoctorForm((prev) => ({ ...prev, [field]: value }));

  const setClinicField = (field) => (value) =>
    setClinicForm((prev) => ({ ...prev, [field]: value }));

  const setAvailField = (weekday, field) => (value) =>
    setAvailability((prev) =>
      prev.map((a) => (a.weekday === weekday ? { ...a, [field]: value } : a))
    );

  const handleSave = async () => {
    setSaving(true);
    try {
      // Only send days that have both start and end times
      const filledSlots = availability.filter(
        (a) => a.start_time && a.end_time
      );

      if (!doctor) {
        // CREATE — clinic first (need clinic_id for doctor FK)
        const { data: clinicData } = await createClinic(clinicForm);
        const clinicId = clinicData?.id;
        const { data: doctorData } = await createDoctor({
          ...doctorForm,
          clinic_id: clinicId,
        });
        if (filledSlots.length) {
          await updateDoctorAvailability(doctorData.id, {
            clinic_id: clinicId,
            availability: filledSlots,
          });
        }
        onSave(doctorData);
      } else {
        // EDIT — clinic + doctor in parallel, then availability
        await Promise.all([
          updateClinic(doctor.clinic_id, clinicForm),
          updateDoctor(doctor.id, doctorForm),
        ]);
        if (filledSlots.length) {
          await updateDoctorAvailability(doctor.id, {
            clinic_id: doctor.clinic_id,
            availability: filledSlots,
          });
        }
        onSave({ ...doctor, ...doctorForm, clinic_name: clinicForm.name });
      }
    } catch {
      // useApi showToast:true already surfaces error toasts
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{doctor ? "Edit Doctor" : "Add Doctor"}</DialogTitle>
        </DialogHeader>

        <Tabs value={formTab} onValueChange={setFormTab}>
          <TabsList className="w-full">
            <TabsTrigger value="doctor" className="flex-1">Doctor Info</TabsTrigger>
            <TabsTrigger value="clinic" className="flex-1">Clinic</TabsTrigger>
            <TabsTrigger value="availability" className="flex-1">Availability</TabsTrigger>
          </TabsList>

          {/* Tab 1 — Doctor Info */}
          <TabsContent value="doctor" className="pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Field label="Full Name" value={doctorForm.name} onChange={setDoctorField("name")} disabled={saving} />
              </div>
              <Field label="Specialty" value={doctorForm.specialty} onChange={setDoctorField("specialty")} disabled={saving} />
              <Field label="APMC Number" value={doctorForm.apmc_number} onChange={setDoctorField("apmc_number")} disabled={saving} />
              <div className="col-span-2">
                <Field label="Degrees (comma-separated)" value={doctorForm.degrees} onChange={setDoctorField("degrees")} placeholder="MBBS, MD" disabled={saving} />
              </div>
              <div className="col-span-2">
                <Field label="Occupation / Title" value={doctorForm.occupation} onChange={setDoctorField("occupation")} placeholder="e.g. Cardiologist" disabled={saving} />
              </div>
              <div className="col-span-2">
                <Field label="Photo URL" value={doctorForm.photo_url} onChange={setDoctorField("photo_url")} disabled={saving} />
              </div>
            </div>
          </TabsContent>

          {/* Tab 2 — Clinic */}
          <TabsContent value="clinic" className="pt-4 space-y-4">
            <Field label="Clinic Name" value={clinicForm.name} onChange={setClinicField("name")} disabled={saving} />
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Address</label>
              <textarea
                className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={clinicForm.address}
                onChange={(e) => setClinicField("address")(e.target.value)}
                placeholder="Full clinic address"
                disabled={saving}
              />
            </div>
          </TabsContent>

          {/* Tab 3 — Availability (start_time / end_time per weekday) */}
          <TabsContent value="availability" className="pt-4 space-y-3">
            <p className="text-xs text-gray-500">Leave blank to mark a day as unavailable. Use 24-hour format (e.g. 09:00, 17:30).</p>
            {DAYS.map(({ label, weekday }) => {
              const entry = availability.find((a) => a.weekday === weekday);
              return (
                <div key={weekday} className="grid grid-cols-3 gap-3 items-center">
                  <span className="text-sm font-medium text-gray-700">{label}</span>
                  <div className="space-y-1">
                    <label className="text-xs text-gray-500">Start</label>
                    <Input
                      type="time"
                      value={entry?.start_time || ""}
                      onChange={(e) => setAvailField(weekday, "start_time")(e.target.value)}
                      disabled={saving}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-gray-500">End</label>
                    <Input
                      type="time"
                      value={entry?.end_time || ""}
                      onChange={(e) => setAvailField(weekday, "end_time")(e.target.value)}
                      disabled={saving}
                    />
                  </div>
                </div>
              );
            })}
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-3 pt-4 border-t mt-4">
          <Button variant="outline" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-orange-600 hover:bg-orange-700"
          >
            {saving ? "Saving..." : doctor ? "Update Doctor" : "Create Doctor"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UnifiedDoctorForm;
